/* import __COLOCATED_TEMPLATE__ from './project-list.hbs'; */
import { gt, readOnly } from '@ember/object/computed';
import { Promise, allSettled } from 'rsvp';
import { inject as service } from '@ember/service';
import Component from '@ember/component';
import { observer, computed } from '@ember/object';
import StateManager from '../mixins/state-manager-mixin';
import { showModal } from 'sh-www/utils/modal-controls';

export default Component.extend(StateManager, {
  classNames: ['project-list-container'],

  // States
  isLoadingProjects: undefined,

  /**
   * @type {Boolean}    True when the XHR to load the blocked users is in flight.
   */
  isLoadingBlockedUsers: false,

  // State Service Default Updates
  stateServiceProperties: 'isLoadingProjects',

  /**
   * Register services
   */
  companyCommandService : service('commands/company-commands'),
  errorTracker: service('error-tracker'),
  sortingUtils: service('sortingUtils'),
  session: service(),
  store: service(),
  currentUser: readOnly('session.currentUser'),

  thread: undefined,
  buyer: computed('thread.etc.relationships.users', function () {
    const usersMeta = this.get('thread.etc.relationships.users');
    if (usersMeta) {
      const buyerMeta = usersMeta.findBy('role', 'buyer');
      return this.get('thread.users').findBy('id', String(buyerMeta.user_id));
    }
    return null;
  }),
  seller: computed('thread.etc.relationships.users', function () {
    const usersMeta = this.get('thread.etc.relationships.users');
    if (usersMeta) {
      const buyerMeta = usersMeta.findBy('role', 'seller');
      return this.get('thread.users').findBy('id', String(buyerMeta.user_id));
    }
    return null;
  }),
  watchers: computed('thread.etc.relationships.users', function () {
  }),

  /**
   * True when the currentUser is a buyer and this seller has been blocked by their company.
   *
   * @type {Boolean}
   */
  _isSellerBlocked: computed('isLoadingBlockedUsers', 'seller', 'currentUser.company.blockedUsers.[]', function() {
    if (this.get('isLoadingBlockedUsers') || this.get('currentUser.isSeller')) {
      return false;
    }

    const company = this.get('store').peekRecord('company', this.get('currentUser.company.id'));
    return company.isUserBlockedByCompany(this.get('seller'));
  }),

  /**
   * If we navigate directly to a thread then we should have all the project info from the model() hook in the route.
   * If we get to the thread from the list in the message center then we need to explicitly load the projects.
   * If we are updating project info then we need to explicitly check as well but we don't want to show the spinner.
   * We want to use forceReload when we already know that the project status has changed and we don't want to wait.
   *
   * @param updating
   * @param forceReload
   * @returns {*}
   * @private
   */
  _loadProjects(updating = false, forceReload = false) {
    const thread = this.get('thread');
    // bail out if we don't have a thread for some reason
    if (!thread) {
      return;
    }
    // if we already loaded the projects for this thread earlier then we are all set
    if (!updating && !forceReload && thread.get('applications.length')) {
      this.set('isLoadingProjects', false);
      this.set('projects', thread.get('applications'));
      return Promise.resolve();
    } else {
      // if we are updating (polling) then we don't need to show the spinner
      if (!updating) {
        this.set('isLoadingProjects', true);
      }
      // otherwise we can reload the thread entirely and get the projects through an include
      return this.get('store').findRecord('thread', thread.get('id'), {include: 'messages,users,users.company,all-time-users,applications,assignment.applications'}).then(thread => {
        // bail out if this component is not around when the promise resolves or the thread has changed
        if(!this || this.get('isDestroying') || this.get('isDestroyed') || (this.get('thread.id') !== thread.get('id'))) {
          return;
        }

        this.set('projects', thread.get('applications'));
        this.set('thread', thread);
        this.set('isLoadingProjects', false);
      }).catch(e => {
        /**
         * This is a special case for this API call.  If this fails due to normal network activity (status = 0 according to Ember)
         * then the we'll have a notFound in the error message AND it will be dirty (needs updating).  This is due
         * to the fact that we make modifications to the thread object only on the client (archivedAt, lastReadAt, and etc.)
         * , and these aren't built to keep in sync with the server, so we have this special error from ember-data
         * which eats the status = 0 AdapterError.
         *
         * TODO - The correct fix would be to keep this in sync with our database correctly, and the
         * Raven filter would filter out the status = 0 exception correctly.
         */

        if (!thread.get('dirtyType') || !(e && /notFound/.test(e.message))) {
          this.get('errorTracker').error(`could not load thread with id ${thread.get('id')} -- ${JSON.stringify(e)}`, this);
          return;
        }
      });
    }
  },

  /**
   * We need to poll for projects to keep up-to-date on project status changes
   * @private
   */
  _pollForProjects() {
    const interval = setInterval(() => {
      this._loadProjects(true, false);
    }, 15000);

    this.set('pollingInterval', interval);
  },

  /**
   * This component doesn't actually get re-inited when switching threads so we need to re-run our project methods.
   */
  watchForProjects: observer('thread', function() {
    clearInterval(this.get('pollingInterval'));

    if (this.get('thread')) {
      this._loadProjects(false, false);
      this._pollForProjects();
    }
  }),

  multipleProjects: gt('projects.length', 1),
  modals: {
    declineProject: {
      projectToDecline: undefined
    }
  },

  actions: {
    launchModalDeclineProject(project) {
      this.set('modals.declineProject.projectToDecline', project);

      this.set('modals.declineProject.afterSuccessfulDeclineCallback', () => {
        return allSettled([this._loadProjects(false, true), this.getNewMessagesAction()]);
      });

      showModal(this, 'project-list/decline-project', {
        project: this.modals.declineProject.projectToDecline,
        afterSuccessfulDeclineCallback: this.modals.declineProject.afterSuccessfulDeclineCallback,
      });
    },
  },

  init() {
    this._super(...arguments);
    this._loadProjects(false, false);
    this._pollForProjects();
  }
});
